import { reactive } from 'vue';
import { changePassword as apiChangePassword, customerSsoUrl, logon, logonApplication, logoutToken, resetPassword, setPassword } from '@/api/core/auth';
import { getApplicationMenus, getApplications, getCurrentCustomer, getOptions, getSlSettings, getUsersInfo } from '@/api/core/portal';
import { useTranslator } from '@/composables/useTranslator';
import { whenOfflineChoiceSettled } from '@/iframe/legacyConnectivity';
import { compareLegacyPassword, enterLegacyApplication, forgetLegacySession, hashLegacyPassword, onLegacySessionAttached, primeLegacySession, restoreLegacySession } from '@/iframe/legacySession';
import { isOnline } from '@/sync/connectivity';
import { resolvePspUrl } from '@/utils/auth/apiBaseUrl';
import authService from '@/utils/auth/authService';
import { consumeSsoHandoff, forgetSsoCustomer } from '@/utils/auth/ssoHandoff';
import { log } from '@/utils/core/vueLogger';
import { NO_COMPLEXITY, parseComplexity, passwordComplexity } from '@/utils/domain/loginModel';
import { APPLICATION_TOKENS, copyApplicationTokens, isWebAppActive, loginErrorAction, sessionFlags, ssoDestination, translateApplications, userFromLogon, withApplicationLogon } from '@/utils/domain/sessionModel';
import { showAlert, showToast } from '@/utils/ui/appMessages';

// The session of the application, owned by Vue since the legacy login and portal pages were decommissioned
// (md/ROUTER_MIGRATION.md "Phase 2c"). Vue logs the user in (form, SSO handoff, remembered session), calls
// what the legacy pages called, and hands the result to the legacy through its gateway (legacySession.js),
// which keeps the storage format and the globals the application reads.

const t = useTranslator();

const PREFILL_KEY = 'ecosLoginPrefill';
const APPLICATIONS = Object.keys(APPLICATION_TOKENS);
const ROLE_KEYS = ['roleSalesId', 'roleDocId', 'roleAssortId', 'roleSales', 'roleDoc', 'roleAssort'];

const emptyContext = () => ({ remember: false, loginAs: false, hiddenApp: false, delegation: null });

export const session = reactive({
	// 'pending' until the boot decided, then 'anonymous' (login page) or 'authenticated'.
	status: 'pending',
	user: null,
	customer: null,
	apps: [],
	options: [],
	settings: null,
	accessRights: {},
	flags: {},
	webAppActive: false,
	context: emptyContext(),
	// Login page state.
	ssoCustomer: null,
	ssoAuto: false,
	panel: 'main',
	passComplexity: null,
	prefill: { userName: '', customerCode: '', remember: false },
	busy: false,
});

let resolveBoot;
let booted;
const resetBoot = () => (booted = new Promise((resolve) => (resolveBoot = resolve)));
resetBoot();
// The online refresh of a restored session, awaited before the legacy is primed with it.
let preparation = Promise.resolve();
// Where an SSO handoff asked to land, and the AUTOLOAD deep link it carries into the application.
let landing = null;
let pendingEntry = null;

export const whenSessionResolved = () => booted;
export const isAuthenticated = () => session.status === 'authenticated';
export const getSessionUser = () => session.user;

export function takeSsoLanding() {
	const target = landing;
	landing = null;
	return target;
}

const sameCode = (a, b) =>
	String(a ?? '')
		.trim()
		.toUpperCase() ===
		String(b ?? '')
			.trim()
			.toUpperCase() && String(a ?? '').trim() !== '';

const errorAlert = (key, type = 'error') => showAlert({ title: t('Error'), text: t(key), type });

function readPrefill() {
	try {
		const { userName = '', customerCode = '' } = JSON.parse(localStorage.getItem(PREFILL_KEY) ?? 'null') ?? {};
		return { userName, customerCode, remember: Boolean(userName || customerCode) };
	} catch {
		return { userName: '', customerCode: '', remember: false };
	}
}

function savePrefill({ userName, customerCode, remember }) {
	try {
		if (remember) localStorage.setItem(PREFILL_KEY, JSON.stringify({ userName, customerCode }));
		else localStorage.removeItem(PREFILL_KEY);
	} catch {
		// storage unavailable
	}
}

const legacyPayload = () => JSON.parse(JSON.stringify({ user: session.user, customer: session.customer, apps: session.apps, options: session.options, settings: session.settings, accessRights: session.accessRights, flags: session.flags }));

function setAuthenticated(data, context) {
	Object.assign(session, {
		user: data.user,
		customer: data.customer ?? null,
		apps: data.apps ?? [],
		options: data.options ?? [],
		settings: data.settings ?? null,
		accessRights: data.accessRights ?? {},
		flags: data.flags ?? sessionFlags({ salesOptions: data.options, settings: data.settings, user: data.user }),
		webAppActive: isWebAppActive(data.options),
		context: { ...emptyContext(), ...context },
		panel: 'main',
		passComplexity: null,
		status: 'authenticated',
	});
	resolveBoot();
	primeCurrentDocument().catch(() => {});
}

function setAnonymous() {
	Object.assign(session, { user: null, customer: null, apps: [], options: [], settings: null, accessRights: {}, flags: {}, webAppActive: false, context: emptyContext(), status: 'anonymous' });
	resolveBoot();
}

// Writes the session into the current legacy document (each iframe load is a new one).
async function primeCurrentDocument() {
	await preparation;
	if (session.status !== 'authenticated') return false;
	return primeLegacySession(legacyPayload(), session.context);
}

/**
 * What the legacy login and portal pages loaded once a user had a base token: customer, process types,
 * options, applications, sl.json, the SALFO / ASSFO / DCRFO tokens, the SFA option of the sales token and the
 * Assort menus. A failed call keeps the stored value; `unauthorized`: the base token was refused.
 */
async function openSession(baseUser, stored = null) {
	let user = { ...baseUser };
	const token = user.token;
	let unauthorized = false;
	const attempt = (label, promise) =>
		promise.catch((error) => {
			if (error?.status === 401) unauthorized = true;
			log('warn', 'session', `${label} failed: ${error.message}`);
			return undefined;
		});

	const [customer, info, options, applications, settings, ...logons] = await Promise.all([attempt('Core/Customers/Current', getCurrentCustomer(token)), attempt('Core/UsersInfo', getUsersInfo(token, user.id)), attempt('Core/Options', getOptions(token)), attempt('Core/Applications', getApplications(token)), attempt('sl.json', getSlSettings()), ...APPLICATIONS.map((application) => attempt(`LogonApplication ${application}`, logonApplication(token, application)))]);

	if (Array.isArray(info?.processTypes)) user.processTypes = info.processTypes;
	APPLICATIONS.forEach((application, index) => {
		if (logons[index]?.token) user = withApplicationLogon(user, logons[index], application);
	});

	const [salesOptions, menus] = await Promise.all([user.tokenSales ? attempt('Core/Options (SALFO)', getOptions(user.tokenSales)) : undefined, user.tokenAssort ? attempt('Core/Menus (ASSFO)', getApplicationMenus(user.tokenAssort)) : undefined]);

	const accessRights = { ...(stored?.accessRights ?? {}) };
	if (Array.isArray(menus)) accessRights.ASSFO = menus.length > 0;
	const finalOptions = Array.isArray(options) ? options : (stored?.options ?? []);
	const finalSettings = settings ?? stored?.settings ?? null;

	return {
		unauthorized,
		user,
		customer: customer ?? stored?.customer ?? null,
		apps: Array.isArray(applications) ? translateApplications(applications, token) : (stored?.apps ?? []),
		options: finalOptions,
		settings: finalSettings,
		accessRights,
		flags: sessionFlags({ salesOptions: Array.isArray(salesOptions) ? salesOptions : finalOptions, settings: finalSettings, user }),
	};
}

// The legacy portal reloaded all of this every time it was shown: a restored session is refreshed once online.
async function refreshSession(stored) {
	try {
		const data = await openSession(stored.user, stored);
		if (data.unauthorized) return expireSession();
		if (session.status !== 'authenticated' || session.user?.id !== data.user.id) return;
		Object.assign(session, { user: data.user, customer: data.customer, apps: data.apps, options: data.options, settings: data.settings, accessRights: data.accessRights, flags: data.flags, webAppActive: isWebAppActive(data.options) });
	} catch (error) {
		log('error', 'session', `refresh failed: ${error.message}`);
	}
}

// login.js: inside a frameset (BO) or with a module, an SSO application handoff hides the chrome, unless it
// is a delegation (connectAs frame of the delegating user's application).
function handoffContext(handoff, remember) {
	const framed = (() => {
		try {
			return window.top !== window;
		} catch {
			return true;
		}
	})();
	const dispatched = Boolean(handoff?.app) && (Boolean(handoff?.token) || handoff?.redirect);
	return { remember, loginAs: Boolean(handoff?.loginAs), hiddenApp: dispatched && (Boolean(handoff?.module) || framed) && !handoff?.delegation, delegation: handoff?.delegation ?? null };
}

// home.js __initHome(): an SSO handoff with an application (?a=...) opens it once the session is there.
function planLanding(handoff) {
	if (!handoff?.app || !(handoff.token || handoff.redirect)) return;
	const destination = ssoDestination({ app: handoff.app, module: handoff.module, param: handoff.param, delegation: Boolean(handoff.delegation) });
	if (destination.psp) {
		const psp = resolvePspUrl(window.location.hostname);
		if (psp && session.user?.token) window.location.href = `${psp}/?u=${encodeURIComponent(session.user.token)}`;
		return;
	}
	landing = destination.route;
	if (destination.autoload) pendingEntry = { autoload: destination.autoload, autoloadParams: destination.autoloadParams ?? null };
}

async function ssoLogin(handoff) {
	const context = handoffContext(handoff, !handoff.loginAs);
	if (!isOnline()) {
		const stored = await restoreLegacySession(handoff.userId ? { id: handoff.userId, customer: handoff.customer ?? '' } : null);
		if (!stored?.user) return false;
		setAuthenticated(stored, context);
		return true;
	}

	let answer;
	try {
		answer = await logonApplication(handoff.token, handoff.app || 'ECOS');
	} catch (error) {
		log('warn', 'session', `SSO logon refused: ${error.message}`);
		showAlert({ title: t('Connection error'), text: t('Authentication failed'), type: 'error' });
		return false;
	}
	if (!(answer?.id > 0)) return false;

	let user = { ...userFromLogon(answer), token: handoff.token };
	const stored = await restoreLegacySession({ id: user.id, customer: user.customer }).catch(() => null);
	if (stored?.user) user = { ...copyApplicationTokens(user, stored.user), customerLoginCode: stored.user.customerLoginCode ?? '' };
	const data = await openSession(user, stored);
	setAuthenticated(data, context);
	return true;
}

// Expired or refused token: the remembered session is useless, back to the login page.
async function expireSession() {
	await forgetLegacySession().catch(() => {});
	window.location.replace('/login');
}

async function decideBoot(handoff) {
	if (handoff?.forgetRemembered) await forgetLegacySession().catch(() => {});

	if (handoff?.token && (await ssoLogin(handoff))) {
		planLanding(handoff);
		return;
	}
	if (handoff?.resetPassword || handoff?.token) return setAnonymous();

	const stored = await restoreLegacySession();
	if (!stored?.user) return setAnonymous();

	if (isOnline()) preparation = refreshSession(stored);
	setAuthenticated(stored, handoffContext(handoff, true));
	planLanding(handoff);
}

/**
 * Called once by main.js, before the iframe exists: the SSO cookies must be read and cleared before the
 * legacy document boots. The session itself is resolved once the legacy gateway is there.
 */
export function bootSession(win = window) {
	const { handoff, ssoCustomer, ssoAuto } = consumeSsoHandoff(win);
	Object.assign(session, { ssoCustomer, ssoAuto, prefill: readPrefill(), panel: handoff?.resetPassword ? 'reset' : 'main' });
	authService.registerTokenSource((tokenType) => session.user?.[tokenType] || null);

	onLegacySessionAttached(() => {
		if (session.status === 'authenticated') primeCurrentDocument().catch(() => {});
	});

	decideBoot(handoff).catch((error) => {
		log('error', 'session', `boot failed: ${error.message}`);
		setAnonymous();
	});
	return booted;
}

// Router bridge dependency: opens the application in the legacy document (once per document). Its
// synchronization starts online or offline: the "stay offline?" question is answered first, as on the portal.
export async function enterApplication(entry = null) {
	if (session.status !== 'authenticated' || !session.webAppActive) return false;
	await primeCurrentDocument();
	await whenOfflineChoiceSettled();
	const pending = entry ?? pendingEntry ?? {};
	const entered = enterLegacyApplication(pending);
	if (entered && pending === pendingEntry) pendingEntry = null;
	return entered;
}

function reportAuthError(error, { fallback = null } = {}) {
	const rule = parseComplexity(error?.body?.message);
	if (rule) {
		session.passComplexity = rule;
		if (rule !== NO_COMPLEXITY) showToast(t('PassComplexityInfo', rule.split('-')), 'warning', true);
	}
	const action = loginErrorAction(error?.code);
	if (action?.panel) {
		session.panel = action.panel;
		return;
	}
	if (action?.alert) {
		errorAlert(error.code, action.alert);
		return;
	}
	if (fallback) fallback();
}

async function offlineLogin({ userName, password, customerCode, remember }) {
	const stored = await restoreLegacySession().catch(() => null);
	const user = stored?.user;
	const known = user && sameCode(user.userName, userName) && [user.customerLoginCode, user.customerCode, user.customer].some((code) => sameCode(code, customerCode));
	if (!known || !(await compareLegacyPassword(password, user.pass))) {
		errorAlert('CheckOldPass');
		return false;
	}
	setAuthenticated(stored, { remember });
	return true;
}

// login.js svcLogin() / __successLogin() / start() / launchApp().
export async function login({ userName, password, customerCode, remember = false }) {
	if (session.busy) return false;
	session.busy = true;
	try {
		savePrefill({ userName, customerCode, remember });
		if (!isOnline()) return await offlineLogin({ userName, password, customerCode, remember });

		let answer;
		try {
			answer = await logon({ userName, password, customerCode });
		} catch (error) {
			reportAuthError(error, { fallback: () => showAlert({ title: t('Connection error'), text: t('Authentication failed'), type: 'error' }) });
			return false;
		}
		if (!(answer?.id > 0)) {
			showAlert({ title: t('Connection error'), text: t('Authentication failed'), type: 'error' });
			return false;
		}

		const previous = await restoreLegacySession({ id: answer.id, customer: answer.customer }).catch(() => null);
		const user = { ...userFromLogon(answer), pass: await hashLegacyPassword(password), customerLoginCode: customerCode, userLastLoginCheck: new Date().toISOString() };
		if (previous?.user) ROLE_KEYS.forEach((key) => (user[key] = previous.user[key] ?? user[key]));

		const data = await openSession(user, previous);
		setAuthenticated(data, { remember });
		return true;
	} finally {
		session.busy = false;
	}
}

// login.js svcResetLogin(): the reset key is mailed.
export async function requestPasswordReset({ email, customer }) {
	if (!isOnline()) {
		errorAlert('OfflineOnlyAction');
		return false;
	}
	session.busy = true;
	try {
		await resetPassword({ email: String(email).trim(), customer });
		session.panel = 'main';
		showAlert({ title: t('Success'), text: t('PleaseCheckYourMail'), type: 'success' });
		return true;
	} catch (error) {
		log('warn', 'session', `ResetPass failed: ${error.message}`);
		errorAlert('Internal server error');
		return false;
	} finally {
		session.busy = false;
	}
}

function complexityRefused(password) {
	if (!session.passComplexity || passwordComplexity(password, session.passComplexity).match) return false;
	errorAlert('PleaseCheckNewPassComplexity');
	return true;
}

// login.js changePassHandler() / svcChangePass(): a new password with the mailed key (?rp=1).
export async function changePasswordWithKey({ email, customer, key, password, confirm }) {
	const refusal = ![email, customer, key, password].every((value) => String(value ?? '').trim()) ? 'PleaseCheckInputs' : password !== confirm ? 'PleaseCheckNewPass' : !isOnline() ? 'OfflineOnlyAction' : null;
	if (refusal) {
		errorAlert(refusal);
		return false;
	}
	if (complexityRefused(confirm)) return false;

	session.busy = true;
	try {
		await apiChangePassword({ email: String(email).trim(), customer, key: String(key).trim(), password });
		session.panel = 'main';
		showAlert({ title: t('Success'), text: t('YourPassHasBeenChanged'), type: 'success' });
		return true;
	} catch (error) {
		reportAuthError(error, { fallback: () => errorAlert('Internal server error') });
		return false;
	} finally {
		session.busy = false;
	}
}

// login.js updatePassHandler() / svcUpdatePass(): replaces an expired password (Auth/Logon ERR-0096).
export async function updateExpiredPassword({ userName, customerCode, oldPassword, newPassword }) {
	if (complexityRefused(newPassword)) return false;
	session.busy = true;
	try {
		await setPassword({ userName, customerCode, oldPassword, newPassword });
		session.panel = 'main';
		showAlert({ title: t('Success'), text: t('YourPassHasBeenChanged'), type: 'success' });
		return true;
	} catch (error) {
		reportAuthError(error, { fallback: () => showAlert({ title: t('An error occured'), type: 'warning' }) });
		return false;
	} finally {
		session.busy = false;
	}
}

export const showLoginPanel = (panel) => (session.panel = panel);

// login.js ssoCustomerHandler(): off to the customer's identity provider, which comes back with ?u=&a=.
export async function customerSso() {
	const code = session.ssoCustomer;
	session.ssoAuto = false;
	if (!code) return false;
	session.busy = true;
	try {
		const url = await customerSsoUrl(code);
		if (url && typeof url === 'string') {
			window.top.location.href = url;
			return true;
		}
		forgetSsoCustomer();
		session.ssoCustomer = null;
		return false;
	} catch (error) {
		log('warn', 'session', `Auth/Customers/Url failed: ${error.message}`);
		showAlert({ title: t('Connection error'), text: t('Internal server error'), type: 'error' });
		return false;
	} finally {
		session.busy = false;
	}
}

// login.js validLanguageSwitch(): the login locale is read at boot (src/i18n/bootstrap.js).
export function changeLoginLanguage(locale) {
	localStorage.setItem('loginLocale', locale);
	window.location.reload();
}

// sessionHelper.logout(): every token invalidated, the remembered session and the SSO customer forgotten.
export async function logout({ extraTokens = [] } = {}) {
	if (!isOnline()) {
		showAlert({ title: t('OfflineMode'), text: t('OfflineOnlyAction'), type: 'info' });
		return false;
	}
	const user = session.user ?? {};
	new Set([user.token, user.tokenSales, user.tokenDoc, user.tokenAssort, ...extraTokens].filter(Boolean)).forEach((token) => logoutToken(token));
	forgetSsoCustomer();
	await forgetLegacySession().catch(() => {});
	setTimeout(() => window.location.replace('/login'), 500);
	return true;
}

// The legacy refused the session (app/vueSession.js requireLogin: web application disabled, re-logon refused).
export function handleLoginRequired() {
	if (session.status !== 'authenticated') return;
	log('warn', 'session', 'The legacy application refused the session');
	expireSession();
}

export function __resetSessionForTests() {
	Object.assign(session, { status: 'pending', user: null, customer: null, apps: [], options: [], settings: null, accessRights: {}, flags: {}, webAppActive: false, context: emptyContext(), ssoCustomer: null, ssoAuto: false, panel: 'main', passComplexity: null, prefill: { userName: '', customerCode: '', remember: false }, busy: false });
	preparation = Promise.resolve();
	landing = null;
	pendingEntry = null;
	resetBoot();
}
